import { useCallback } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { ShiftDetailsPageRedesign } from '~/constants/ff.posthog';
import {
  setConfirmCandidateSource,
  type TConfirmCandidateSource,
} from '~/domain/ConfirmCandidateSource';
import { routes } from '~/routes';
import { useFeatureFlag } from './useFeatureFlag';

interface INavigateToConfirmCandidateOptions {
  /** Shift the office is confirming for. Scopes the recorded source. */
  shiftId: string;
  /** The applicant the legacy confirm screen opens on. */
  candidateId: string;
  /**
   * How many applicants the shift has. With several, the redesign lands on its
   * candidate list instead of opening `candidateId`, so the office can compare
   * them before picking one.
   */
  candidateCount: number;
  /**
   * Whether the shift is an onsite interview. The redesign leaves those on the
   * legacy screens (`shiftDetailsPageForJob`), which have no list to land on —
   * the shift route redirects to the first applicant — so they always open
   * `candidateId`.
   */
  isOnsiteInterview: boolean;
  /** What drove the click, reported later on `confirm_clicked` (GOTU-4906). */
  source: TConfirmCandidateSource;
}

/**
 * Opens a pending shift's applicants for confirming, and records what drove the
 * click in the same call (GOTU-4906).
 *
 * The two belong together: the source is read back at confirm time, so a
 * navigation that forgot to record one reports no attribution at all. Keeping
 * them in one function stops the pair drifting apart as call sites are edited.
 *
 * Where it lands depends on what can show the applicants:
 * - Redesign on, several applicants, not an interview: the shift route, where
 *   the redesign lists them all (GOTU-13265). The candidate route would open
 *   only `candidateId`.
 * - Everything else: the candidate route, on `candidateId`. Legacy screens
 *   (flag off, or an interview with it on) redirect the shift route to the
 *   first applicant, which would drop the caller's favourite-first pick.
 */
export const useNavigateToConfirmCandidate = () => {
  const navigate = useNavigate();
  const isRedesignEnabled = useFeatureFlag(ShiftDetailsPageRedesign);

  return useCallback(
    ({
      shiftId,
      candidateId,
      candidateCount,
      isOnsiteInterview,
      source,
    }: INavigateToConfirmCandidateOptions) => {
      setConfirmCandidateSource({ source, shiftId });

      if (isRedesignEnabled && candidateCount > 1 && !isOnsiteInterview) {
        navigate(generatePath(routes.openJobDetails, { jobId: shiftId }));
        return;
      }

      navigate(generatePath(routes.confirmCandidate, { jobId: shiftId, candidateId }));
    },
    [isRedesignEnabled, navigate],
  );
};
